import { FC, ReactNode, createContext, useMemo } from 'react';

import { UserData, V2OrgData } from '@partoohub/api-client';

import { V2Subscriptions } from 'app/api/types';
import LoadingView from 'app/common/components/LoadingView';
import useMe from 'app/common/hooks/queries/useMeUncamel';
import { useHandleLanguageChanges } from 'app/pages/app/AppDataProvider/hooks/private/useHandleLanguageChanges';
import { useSubscriptionsQuery } from 'app/pages/app/AppDataProvider/hooks/private/useSubscriptionsQuery';

import useOrganization from '../../../common/hooks/queries/useOrganization';

export type AppDataContextType = {
    me: UserData;
    organization: V2OrgData;
    subscriptions: V2Subscriptions;
};

export const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

type AppDataProviderType = {
    children: ReactNode;
};

/**
 * App data context provider
 * The app child won't be rendered until all the mandatory data is fetched
 */
export const AppDataProvider: FC<AppDataProviderType> = ({ children }) => {
    const { data: me, isLoading: isLoadingMe } = useMe();
    const { data: organization, isLoading: isLoadingOrganization } = useOrganization(
        me?.org_id ?? 0,
        !!me,
    );
    const { data: subscriptions, isLoading: isLoadingSubscriptions } = useSubscriptionsQuery();

    const isDataAvailable =
        !(isLoadingMe || isLoadingOrganization || isLoadingSubscriptions) &&
        !!me &&
        !!organization &&
        !!subscriptions;
    useHandleLanguageChanges(me);

    const value: AppDataContextType | undefined = useMemo(
        () => (isDataAvailable ? { me, organization, subscriptions } : undefined),
        [me, organization, subscriptions, isDataAvailable],
    );

    if (!value) {
        return <LoadingView />;
    }

    return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};
